import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle, Loader2, Mail, User, Users } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AmbassadorPage = () => {
  const [form, setForm] = useState({ name: "", email: "" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email) {
      toast.error("Please enter your email address");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, {
        ...form,
        category: "General Subscribers",
      });
      setSubmitted(true);
      toast.success("You're on the list!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Signup failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-fade-in">
      <section className="bg-[#EEF8F2] text-[#1a1a1a] border-b border-[#42D78A]/20 py-20 md:py-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-16 h-16 rounded-full bg-[#42D78A]/10 flex items-center justify-center mx-auto mb-6">
            <Users className="w-8 h-8 text-[#42D78A]" />
          </div>
          <p className="text-[#42D78A] text-sm tracking-[0.2em] uppercase mb-4 font-medium" style={{ fontFamily: "'Montserrat', sans-serif" }}>Coming Soon</p>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Become an Ambassador
          </h1>
          <p className="text-gray-600 max-w-2xl mx-auto leading-relaxed" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            Ambassador opportunities are on the way. Subscribe for updates and be the first to know when applications open.
          </p>
        </div>
      </section>

      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-8 lg:gap-12 items-start">
          <div className="rounded-2xl border border-gray-100 bg-white p-6 md:p-8 shadow-sm">
            {submitted ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-[#42D78A]/10 rounded-full flex items-center justify-center mx-auto mb-5">
                  <CheckCircle className="w-8 h-8 text-[#42D78A]" />
                </div>
                <h2 className="text-2xl font-bold text-[#1a1a1a] mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  You&apos;re subscribed
                </h2>
                <p className="text-gray-600" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  We&apos;ll keep you informed as the ambassador program gets closer to launch.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="ambassador-newsletter-form">
                <div>
                  <Label className="text-[#1a1a1a] font-medium text-sm" style={{ fontFamily: "'Outfit', sans-serif" }}>Name</Label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      value={form.name}
                      onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Your name"
                      className="h-12 pl-10 rounded-lg border-gray-200"
                      disabled={loading}
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-[#1a1a1a] font-medium text-sm" style={{ fontFamily: "'Outfit', sans-serif" }}>Email *</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="you@email.com"
                      className="h-12 pl-10 rounded-lg border-gray-200"
                      disabled={loading}
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#42D78A] hover:bg-[#3bc27c] text-[#1a1a1a] rounded-full py-6 text-base font-bold transition-all hover:-translate-y-0.5"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                >
                  {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Subscribing...</> : "Notify Me"}
                </Button>
              </form>
            )}
          </div>

          <div className="rounded-2xl border border-gray-100 bg-white p-6 md:p-8 shadow-sm">
            <h2 className="text-2xl font-bold text-[#1a1a1a] mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Join in the meantime
            </h2>
            <p className="text-gray-600 leading-relaxed mb-6" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              You can still create an account now to save businesses, follow markets, and be part of 614 Shop Local as new community features roll out.
            </p>
            <Link to="/register">
              <Button className="bg-[#1a1a1a] hover:bg-black text-white rounded-full px-8 py-5 text-sm font-bold" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Create an Account
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AmbassadorPage;
